/**
 * Reservation & offer mutations.
 *
 * All NON-OPTIMISTIC: reservations feed booking/billing decisions and offers
 * are guest-facing commitments, so the UI waits for backend confirmation and
 * then invalidates. We `setQueryData` for the freshly returned entity (the
 * canonical, complete object) AND invalidate the list so it re-sorts.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type OfferDraft,
  offerDraftSchema,
  type Reservation,
  reservationSchema,
} from '../responses';
import type {
  CreateReservationBody,
  DraftOfferBody,
  TransitionReservationBody,
  UpdateReservationBody,
} from './_types';

function useReservationCacheSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (reservation: Reservation) => {
    queryClient.setQueryData(queryKeys.reservation(tenantId, hotelId, reservation.id), reservation);
    void queryClient.invalidateQueries({ queryKey: queryKeys.reservations(tenantId, hotelId) });
  };
}

/** `POST /reservations` — create a reservation request. */
export function useCreateReservation(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Reservation, unknown, CreateReservationBody> {
  const client = useApiClient();
  const sync = useReservationCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async (body: CreateReservationBody) => {
      const result = await client.POST('/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations', {
        params: { path: { tenantId, hotelId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, reservationSchema);
    },
    onSuccess: sync,
  });
}

export interface UpdateReservationVars {
  reservationId: string;
  body: UpdateReservationBody;
}

/** `PATCH /reservations/:reservationId`. */
export function useUpdateReservation(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Reservation, unknown, UpdateReservationVars> {
  const client = useApiClient();
  const sync = useReservationCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ reservationId, body }: UpdateReservationVars) => {
      const result = await client.PATCH(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/{reservationId}',
        { params: { path: { tenantId, hotelId, reservationId } }, body, parseAs: 'json' },
      );
      return unwrap(result, reservationSchema);
    },
    onSuccess: sync,
  });
}

export interface TransitionReservationVars {
  reservationId: string;
  body: TransitionReservationBody;
}

/** `POST /reservations/:reservationId/transition` — drive the status machine. */
export function useTransitionReservation(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Reservation, unknown, TransitionReservationVars> {
  const client = useApiClient();
  const sync = useReservationCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ reservationId, body }: TransitionReservationVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/{reservationId}/transition',
        { params: { path: { tenantId, hotelId, reservationId } }, body, parseAs: 'json' },
      );
      return unwrap(result, reservationSchema);
    },
    onSuccess: sync,
  });
}

function useOfferCacheSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (offer: OfferDraft) => {
    queryClient.setQueryData(queryKeys.reservationOffer(tenantId, hotelId, offer.id), offer);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.reservation(tenantId, hotelId, offer.reservationRequestId),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.reservations(tenantId, hotelId) });
  };
}

export interface DraftOfferVars {
  reservationId: string;
  body: DraftOfferBody;
}

/** `POST /reservations/:reservationId/offers` — draft an offer for a reservation. */
export function useDraftOffer(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OfferDraft, unknown, DraftOfferVars> {
  const client = useApiClient();
  const sync = useOfferCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ reservationId, body }: DraftOfferVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/{reservationId}/offers',
        { params: { path: { tenantId, hotelId, reservationId } }, body, parseAs: 'json' },
      );
      return unwrap(result, offerDraftSchema);
    },
    onSuccess: sync,
  });
}

/** Internal: build an offer lifecycle mutation (send/accept/decline) for an offerId. */
function useOfferLifecycle(
  tenantId: string,
  hotelId: string,
  segment: 'send' | 'accept' | 'decline',
): UseMutationResult<OfferDraft, unknown, { offerId: string }> {
  const client = useApiClient();
  const sync = useOfferCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ offerId }: { offerId: string }) => {
      const path =
        `/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/offers/{offerId}/${segment}` as
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/offers/{offerId}/send'
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/offers/{offerId}/accept'
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/offers/{offerId}/decline';
      const result = await client.POST(path, {
        params: { path: { tenantId, hotelId, offerId } },
        parseAs: 'json',
      });
      return unwrap(result, offerDraftSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /reservations/offers/:offerId/send` — send a drafted offer to the guest. */
export function useSendOffer(tenantId: string, hotelId: string) {
  return useOfferLifecycle(tenantId, hotelId, 'send');
}

/** `POST /reservations/offers/:offerId/accept`. */
export function useAcceptOffer(tenantId: string, hotelId: string) {
  return useOfferLifecycle(tenantId, hotelId, 'accept');
}

/** `POST /reservations/offers/:offerId/decline`. */
export function useDeclineOffer(tenantId: string, hotelId: string) {
  return useOfferLifecycle(tenantId, hotelId, 'decline');
}
